import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  listFrameworkRequirements,
  type ComplianceRequirement,
  type FrameworkKey,
} from "@/lib/api";

/**
 * The standards as trees — ISO clauses 4–10 + Annex A, and the SOC 2 common
 * criteria — with per-requirement coverage (how many applicable controls map
 * to it). Zero-coverage leaves are the real gaps; the overview lists them too.
 */
export function ComplianceFrameworksPage() {
  const [key, setKey] = useState<FrameworkKey>("iso27001");
  const { data, isLoading } = useQuery({
    queryKey: ["compliance-requirements", key],
    queryFn: () => listFrameworkRequirements(key),
  });

  const roots = useMemo(() => {
    if (!data) return [];
    const byParent = new Map<string | null, ComplianceRequirement[]>();
    for (const r of data.requirements) {
      const list = byParent.get(r.parentId) ?? [];
      list.push(r);
      byParent.set(r.parentId, list);
    }
    return (byParent.get(null) ?? []).map((root) => ({
      root,
      children: byParent.get(root.id) ?? [],
      byParent,
    }));
  }, [data]);

  return (
    <div>
      <header className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="page-title">Frameworks</h1>
          <p className="mt-2 text-text-secondary">
            What the standards ask, and which of our controls answer. Provisional rows await
            the licensed text.
          </p>
        </div>
        <div className="flex gap-1 rounded-token-md border border-border bg-bg-card p-1">
          {(
            [
              ["iso27001", "ISO 27001"],
              ["soc2", "SOC 2"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setKey(k)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                key === k ? "bg-accent-muted text-accent" : "text-text-secondary hover:text-text-heading"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {isLoading || !data ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : (
        <div className="space-y-4">
          {roots.map(({ root, children, byParent }) => (
            <section key={root.id} className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
              <h2 className="border-b border-border px-4 py-3 font-display text-sm font-semibold text-text-heading">
                {root.ref} — {root.title}
              </h2>
              <ul>
                {children.map((r) => (
                  <RequirementRow key={r.id} r={r} coverage={data.coverage} byParent={byParent} depth={0} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function RequirementRow({
  r,
  coverage,
  byParent,
  depth,
}: {
  r: ComplianceRequirement;
  coverage: Record<string, number>;
  byParent: Map<string | null, ComplianceRequirement[]>;
  depth: number;
}) {
  const children = byParent.get(r.id) ?? [];
  const count = coverage[r.id] ?? 0;
  const isLeaf = r.kind !== "group";
  return (
    <>
      <li
        className="flex items-center gap-3 border-b border-border-light px-4 py-2 text-sm"
        style={{ paddingLeft: `${1 + depth * 1.25}rem` }}
      >
        <span className="w-16 shrink-0 font-medium text-text-heading">{r.ref}</span>
        <span className="min-w-0 flex-1 truncate text-text-secondary">{r.title}</span>
        {r.sourceStatus === "provisional" ? (
          <span className="shrink-0 text-[11px] text-text-muted" title="Not yet checked against the licensed standard">
            provisional
          </span>
        ) : (
          <span className="shrink-0 text-[11px] text-success">verified</span>
        )}
        {isLeaf && (
          <span
            className={`w-20 shrink-0 text-right text-xs ${count > 0 ? "text-text-muted" : "font-medium text-warning"}`}
          >
            {count > 0 ? `${count} control${count === 1 ? "" : "s"}` : "uncovered"}
          </span>
        )}
      </li>
      {children.map((c) => (
        <RequirementRow key={c.id} r={c} coverage={coverage} byParent={byParent} depth={depth + 1} />
      ))}
    </>
  );
}
